import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LANG } from '@/i18n';
import { pickI18nString } from '@/lib/i18nUtils';

export interface ConversationTemplate {
  id: string;
  name: string;
  description: string;
  template_type: string;
  duration_months: number;
  stages: ConversationStage[];
  skill_frameworks: SkillFramework[];
}

export interface ConversationStage {
  id: string;
  name: string;
  description: string;
  stage_type: 'review' | 'checkin' | 'objective_setting' | 'skills_assessment';
  order_index: number;
  timing_weeks: number | null;
  is_optional: boolean;
  questions: ConversationQuestion[];
  objectives: ConversationObjective[];
}

export interface ConversationQuestion {
  id: string;
  question_text: string;
  question_type_id: string;
  respondent_type: 'subject' | 'manager' | 'both';
  order_index: number;
  is_required: boolean;
  options: any;
  question_type?: {
    name: string;
    description: string;
  };
}

export interface ConversationObjective {
  id: string;
  objective_text: string;
  is_placeholder: boolean;
  order_index: number;
}

export interface SkillFramework {
  id: string;
  name: string;
  description: string;
  is_default: boolean;
  categories: SkillCategory[];
}

export interface SkillCategory {
  id: string;
  name: string;
  description: string;
  category_type: 'core' | 'functional';
  order_index: number;
  skills: Skill[];
}

export interface Skill {
  id: string;
  name: string;
  description: string;
  order_index: number;
}

export const useConversationTemplatesData = (toolId?: string) => {
  const { i18n } = useTranslation();
  const lang = i18n.language || DEFAULT_LANG;
  return useQuery({
    queryKey: ['conversation-templates', toolId, lang],
    queryFn: async () => {
      let query = supabase
        .from('conversation_templates')
        .select(`
          *,
          stages:conversation_stages(
            *,
            questions:conversation_questions(
              *,
              question_type:question_types(name, name_i18n, description, description_i18n)
            ),
            objectives:conversation_objectives(*)
          )
        `)
        .order('created_at', { ascending: false });

      if (toolId) {
        query = query.eq('tool_id', toolId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (
        data?.map((template: any) => ({
          ...template,
          name: pickI18nString(template.name_i18n, template.name, lang, DEFAULT_LANG),
          description: pickI18nString(template.description_i18n, template.description, lang, DEFAULT_LANG),
          stages: (template.stages || [])
            .sort((a: any, b: any) => a.order_index - b.order_index)
            .map((stage: any) => ({
              ...stage,
              name: pickI18nString(stage.name_i18n, stage.name, lang, DEFAULT_LANG),
              description: pickI18nString(stage.description_i18n, stage.description, lang, DEFAULT_LANG),
              questions: (stage.questions || []).sort((a: any, b: any) => a.order_index - b.order_index).map((q: any) => ({
                ...q,
                question_text: pickI18nString(q.question_text_i18n, q.question_text, lang, DEFAULT_LANG),
                question_type: q.question_type
                  ? {
                      ...q.question_type,
                      name: pickI18nString(q.question_type.name_i18n, q.question_type.name, lang, DEFAULT_LANG),
                      description: pickI18nString(q.question_type.description_i18n, q.question_type.description, lang, DEFAULT_LANG),
                    }
                  : undefined,
              })),
              objectives: (stage.objectives || []).sort((a: any, b: any) => a.order_index - b.order_index),
            })),
        })) || []
      );
    },
    enabled: !!toolId
  });
};

export const useSkillFrameworksData = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || DEFAULT_LANG;
  return useQuery({
    queryKey: ['skill-frameworks', lang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('skill_frameworks')
        .select(`
          *,
          categories:skill_categories(
            *,
            skills:skills(*)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (
        data?.map((framework: any) => ({
          ...framework,
          name: pickI18nString(framework.name_i18n, framework.name, lang, DEFAULT_LANG),
          description: pickI18nString(framework.description_i18n, framework.description, lang, DEFAULT_LANG),
          categories: (framework.categories || [])
            .sort((a: any, b: any) => a.order_index - b.order_index)
            .map((category: any) => ({
              ...category,
              name: pickI18nString(category.name_i18n, category.name, lang, DEFAULT_LANG),
              description: pickI18nString(category.description_i18n, category.description, lang, DEFAULT_LANG),
              skills: (category.skills || []).sort((a: any, b: any) => a.order_index - b.order_index).map((s: any) => ({
                ...s,
                name: pickI18nString(s.name_i18n, s.name, lang, DEFAULT_LANG),
                description: pickI18nString(s.description_i18n, s.description, lang, DEFAULT_LANG),
              })),
            }))
        })) || []
      );
    },
  });
};

export const useConversationTemplateData = (templateId: string) => {
  const { i18n } = useTranslation();
  const lang = i18n.language || DEFAULT_LANG;
  return useQuery({
    queryKey: ['conversation-template', templateId, lang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('conversation_templates')
        .select(`
          *,
          stages:conversation_stages(
            *,
            questions:conversation_questions(
              *,
              question_type:question_types(name, name_i18n, description, description_i18n)
            ),
            objectives:conversation_objectives(*)
          ),
          skill_frameworks:template_skill_frameworks(
            framework:skill_frameworks(
              *,
              categories:skill_categories(
                *,
                skills:skills(*)
              )
            )
          )
        `)
        .eq('id', templateId)
        .single();

      if (error) throw error;

      return {
        ...data,
        name: pickI18nString(data.name_i18n, data.name, lang, DEFAULT_LANG),
        description: pickI18nString(data.description_i18n, data.description, lang, DEFAULT_LANG),
        stages: (data.stages || [])
          .sort((a: any, b: any) => a.order_index - b.order_index)
          .map((stage: any) => ({
            ...stage,
            name: pickI18nString(stage.name_i18n, stage.name, lang, DEFAULT_LANG),
            description: pickI18nString(stage.description_i18n, stage.description, lang, DEFAULT_LANG),
            questions: (stage.questions || []).sort((a: any, b: any) => a.order_index - b.order_index).map((q: any) => ({
              ...q,
              question_text: pickI18nString(q.question_text_i18n, q.question_text, lang, DEFAULT_LANG),
              question_type: q.question_type
                ? {
                    ...q.question_type,
                    name: pickI18nString(q.question_type.name_i18n, q.question_type.name, lang, DEFAULT_LANG),
                    description: pickI18nString(q.question_type.description_i18n, q.question_type.description, lang, DEFAULT_LANG),
                  }
                : undefined,
            })),
            objectives: (stage.objectives || []).sort((a: any, b: any) => a.order_index - b.order_index)
          })),
        skill_frameworks: (data.skill_frameworks || []).map((sf: any) => ({
          ...sf.framework,
          name: pickI18nString(sf.framework.name_i18n, sf.framework.name, lang, DEFAULT_LANG),
          description: pickI18nString(sf.framework.description_i18n, sf.framework.description, lang, DEFAULT_LANG),
          categories: (sf.framework.categories || [])
            .sort((a: any, b: any) => a.order_index - b.order_index)
            .map((category: any) => ({
              ...category,
              name: pickI18nString(category.name_i18n, category.name, lang, DEFAULT_LANG),
              description: pickI18nString(category.description_i18n, category.description, lang, DEFAULT_LANG),
              skills: (category.skills || []).sort((a: any, b: any) => a.order_index - b.order_index).map((s: any) => ({
                ...s,
                name: pickI18nString(s.name_i18n, s.name, lang, DEFAULT_LANG),
                description: pickI18nString(s.description_i18n, s.description, lang, DEFAULT_LANG),
              }))
            }))
        }))
      };
    },
    enabled: !!templateId
  });
};